import gql from 'graphql-tag'
import {useQuery} from '@apollo/client'
import {useUniqueAddressessCount, useSelectedIndividualHealthFilters} from '../PopulationDensityHelper'
import {useStatesAndHealthConditions} from '../../../../../store/hooks/breadCrumbs'
import {getState} from '../../../../../shared/helpers/getState'
import {usePermission} from '../../../../../store/hooks/permission'
import {useFilterManagement} from '../../../../../store/hooks/useFilterManagement'
import {getBenefitsQueryFields} from '../helpers'
import {usePersona} from '../../../../../store/hooks/persona'
import {useRolePermissions} from '../../../../../hooks/useRolesPermissions'

/**
 * Get risk percentile fields as riskPercentile0, riskPercentile1, etc.
 * @param {Array} healthFilters - Array of {key, name} objects from getSelectedIndividualHealthFilters
 * @returns {string} Space-separated string of generic risk percentile field names (riskPercentile0, riskPercentile1, etc.)
 */
const getIndividualHealthRiskFields = (healthFilters) => {
  return healthFilters?.map((filter, index) => `riskPercentile${index}`).join(' ') || ''
}

const UniqueHouseholds = (benefitsFields, persona, healthRiskFields) => {
  return gql`
    query uniqueHouseholds($filters: Filters, $diseases: [String], $benefits: [BenefitEligibility]) {
      uniqueHouseholds(filters: $filters, diseases: $diseases, benefits: $benefits) {
        latitude
        longitude
        address
        city
        state
        numberOfPeople
        squareFeet
        zip
        age
        householdType
        ${benefitsFields}
        ${healthRiskFields}
        ${persona}
      }
    }
  `
}

export const useGetUniqueHouseholds = (uniqueAddressesCount, loading) => {
  const selectedHealthFilters = useSelectedIndividualHealthFilters() // New filter system
  const {getQueryParams, getOutreachParams} = useFilterManagement()
  const {filters} = useStatesAndHealthConditions()
  const {isDeliveryAdmin} = useRolePermissions()
  const {permission} = usePermission()
  const dataAggregationValue = permission?.healthEquityConditionList?.dataAggregationPermissions
  const appliedFilterParams = getQueryParams()
  const uniqueAddressMaximum = useUniqueAddressessCount()
  const currentStateFromContext = filters && getState(filters)

  // Get outreach params
  const outreach = getOutreachParams()
  const benefitsData = outreach.type === 'benefits' ? outreach.data : []
  const benefitsFields = getBenefitsQueryFields(benefitsData)

  const {personasToggle, personas} = usePersona()

  const personaString = personasToggle
    ? `persona 
        personaCohort`
    : ''
  const selectedPersonas = personasToggle ? {persona: personas} : {}

  // Get health risk fields and disease keys for query
  const healthRiskFields = getIndividualHealthRiskFields(selectedHealthFilters)
  const diseasesForVariables =
    selectedHealthFilters.length > 0 ? selectedHealthFilters.map((f) => f.key) : ['No Selection']

  const {
    data: uniqueHouseholds,
    loading: loadingUniqueHouseholds,
    error: errorUniqueHouseholds,
    refetch: refetchUniqueHouseholds
  } = useQuery(UniqueHouseholds(benefitsFields, personaString, healthRiskFields), {
    variables: {
      filters: {
        ...appliedFilterParams,
        state: currentStateFromContext,
        ...selectedPersonas
      },
      diseases: diseasesForVariables,
      benefits: benefitsData
    },
    skip:
      loading ||
      !uniqueAddressesCount ||
      uniqueAddressesCount > uniqueAddressMaximum ||
      (!isDeliveryAdmin() && !dataAggregationValue?.includes(1) && !dataAggregationValue?.includes(4)),

    partialRefetch: true,
    notifyOnNetworkStatusChange: true
  })

  return {uniqueHouseholds, loadingUniqueHouseholds, errorUniqueHouseholds, refetchUniqueHouseholds}
}

/**
 * List of functions and their purpose:
 *
 * - getIndividualHealthRiskFields: Generates generic risk percentile field names (riskPercentile0, riskPercentile1, etc.) for GraphQL query based on number of selected filters.
 * - UniqueHouseholds: Constructs a GraphQL query to fetch unique household data based on filters, diseases, and benefits.
 * - getUniqueHouseholds: Executes the unique households query with dynamic filters and returns the query results using the new Individual Health filter management system.
 */
